import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import { getCurrentShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { SetShipTo } from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import clearSearch from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/ClearSearch";
import toggleFiltersOpen from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/ToggleFiltersOpen";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import { OrderStatusMappingModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SearchFieldWrapper, {
    SearchFieldWrapperStyles,
} from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import SearchTextField, { SearchTextFieldStyles } from "@insite/content-library/Widgets/OrderHistory/SearchTextField";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Icon from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";


interface OwnProps extends WidgetProps { }

const mapStateToProps = (state: ApplicationState) => {
    return {
        productErpNumber: state.pages.orderHistory.getOrdersParameter.productErpNumber,
        currentShipTosDataView: getCurrentShipTosDataView(state),
        parameter: state.pages.orderHistory.getOrdersParameter,
        orderStatusMappings: getOrderStatusMappingDataView(state).value,
    };
};

const mapDispatchToProps = {
    clearSearch,
    updateSearchFields,
    loadCurrentShipTos,
    getAutocompleteModel,
    toggleFiltersOpen
};

// type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderHistorySearchClearButtonStyles {
    clearButton?: ButtonPresentationProps;
    wrapper?: SearchFieldWrapperStyles;
    filterOrderButton?: ButtonPresentationProps;
    customWrapper?: InjectableCss;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    datePickersWrapper?: InjectableCss;
    fromDate?: DatePickerPresentationProps;
    toDate?: DatePickerPresentationProps;
    select?: SelectProps;
    valueOrderTotalCSS?: InjectableCss;
}


export const clearButtonStyles: OrderHistorySearchClearButtonStyles = {
    clearButton: {
        variant: "secondary",
        css: css`
            color: #868686;
            font-family: 'Roboto', sans-serif;
            font-size: 15px;
            letter-spacing: 0;
            line-height: 21px;
            text-align: center;
            text-decoration: underline;
            display: inline-flex;
            justify-content: center;
            align-items: center;
            border: none;
            border: none !important;
            background: none !important;
            padding: 0;

            span {
                color: #868686 !important;
                font-weight: normal !important;
            }

            &:hover,
            &:active,
            &:focus {
                color: #868686 !important;
                border: none !important;
                background: none !important;
                padding: 0;

                span {
                    color: #868686 !important;
                    font-weight: normal !important;
                }
            }
        `,
    },
    filterOrderButton: {
        color: "primary",
        buttonType: "outline",
        css: css`
            margin-bottom: 20px;
            width: 100%;
            display: inline-flex;
            justify-content: center;
            align-items: center;
            text-transform: uppercase;
        `,
    },
    customWrapper: {
        css: css`
            margin-top: 25px;
            padding-top: 25px;
            border-top: 1px solid #D7D7D7;
            display: flex;
            flex-direction: column;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
    datePickersWrapper: {
        css: css`
            display: flex;
        `,
    },
    fromDate: {
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            formField: css`
                padding-right: 5px;
            `,
        },
    },
    toDate: {
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            formField: css`
                padding-left: 5px;
            `,
        },
        labelProps: {
            color: "common.background",
        },
    },
    valueOrderTotalCSS: {
        css: css`
            display: none;
        `,
    },

};

const styles = clearButtonStyles;

const OrderHistorySearchClearButton = (props: Props) => {
    const { productErpNumber, getAutocompleteModel, updateSearchFields, currentShipTosDataView, parameter, orderStatusMappings, loadCurrentShipTos } = props;
    const [shipToValue, setValue] = React.useState("");
    const [setfromDate, setFDValue] = React.useState("");
    const [settoDate, setTDValue] = React.useState("");
    const [poNumberValue, setPONumber] = React.useState("");
    const [statusValue, setStatus] = React.useState("");
    const [valueOrderTotal, setValueOrderTotal] = React.useState("");
    const [orderNumber, setOrderNumber] = React.useState("");
    const [statusOperatorAmountValue, setOperatorAmountValue] = React.useState("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const orderTotalOperators = ["Greater Than", "Less Than", "Equal To"];
    const fromDate = convertDateOnlyStringToDate(parameter.fromDate);
    const toDate = convertDateOnlyStringToDate(parameter.toDate);
    const [prodErpNumber,setproductErpNumber]= React.useState("");
    const [searchInput, setSearchInput] = React.useState("");
    const [noOptionsPresent, setNoOptionsPresent] = useState(false);

    const clearSearch = () => {
        setPONumber("");
        setValue("");
        setTDValue("");
        setFDValue("");
        setStatus("");
        setValueOrderTotal("");
        setOrderNumber("");
        setOptions([]);
        setOperatorAmountValue("");
        setSearchInput("");
    };

    useEffect(() => {
        if (!currentShipTosDataView.value) {
            loadCurrentShipTos();
        }
        getAutocompleteModel;
    }, []);

    const setFilter = () => {
        const loadParameter = {
            ...parameter,
            fromDate: setfromDate || undefined,
            toDate: settoDate || undefined,
            customerSequence: shipToValue === "-1" || shipToValue === "" ? "-1" : shipToValue === "Use Billing Address" ? undefined : shipToValue,
            poNumber: poNumberValue || undefined,
            status: statusValue ? statusValue.split(",") : undefined,
            displayName: statusValue ? findDisplayName(statusValue, orderStatusMappings) : undefined,
            orderTotalOperator: statusOperatorAmountValue || undefined,
            orderNumber: orderNumber || undefined,
            orderTotal: parseFloat(valueOrderTotal) || undefined,
            productErpNumber:prodErpNumber||undefined,
            searchInput: searchInput || undefined,
        };
        updateSearchFields(loadParameter);
        props.toggleFiltersOpen();
    };

    const handleChange = React.useCallback((e: any) => {
        setValue(e.target.value);
    }, []);


    const handlePOChange = React.useCallback((e: any) => {
        setPONumber(e.target.value);
    }, []);

    const handleSearchInput = React.useCallback((e: any) => {
        setSearchInput(e.target.value);
    }, []);

    const handleOrderNumberChange = React.useCallback((e: any) => {
        setOrderNumber(e.target.value);
    }, []);

    const onSelectionChangeHandler = React.useCallback((e: any) => {
        // setOrderNumber(e);
        setproductErpNumber(e);
        // updateSearchFields({ productErpNumber: value });
    }, []);

    const debouncedSearchProducts = debounce((query: string) => {

        getAutocompleteModel({
            query,
            onSuccess: result => {
                if (!result?.products) {
                    return;
                }

                const newOptions = result.products.map(product => ({
                    optionText: product.erpNumber,
                    optionValue: product.erpNumber || undefined,
                    rowChildren: (
                        <StyledWrapper {...styles.optionWrapper}>
                            <StyledWrapper {...styles.imageWrapper}>
                                <LazyImage {...styles.productImage} src={product.image} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.autocompleteTitleText}>{product.title}</Typography>
                                <Typography {...styles.autocompleteErpText}>{product.erpNumber}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    ),
                }));
                setOptions(newOptions);
            },
            onComplete(resultProps) {
                if (resultProps?.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps?.apiResult);
                }
            },
        });
    }, 300);

    const updateFilterSearchClass = () => {
        const filterSearchElement = document.querySelector("[data-test-selector='filter-search']");
        const ulElement = filterSearchElement?.querySelector("ul");
        if (ulElement) {
            if (ulElement.childElementCount === 0) {
                filterSearchElement?.classList.add("no-options-present");
            } else if (ulElement.childElementCount === 1) {
                filterSearchElement?.classList.add("no-options-present");
            } else if (ulElement.childElementCount > 1) {
                filterSearchElement?.classList.remove("no-options-present");
            }
        }
    };

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        debouncedSearchProducts(event.target.value);
    };
    
    const checkInputValue = () => {
        updateFilterSearchClass();
    };

    const shipToOptions = currentShipTosDataView.value || [];

    const fromDateChangeHandler = React.useCallback((e: any) => {
        setFDValue(convertDateToDateOnlyString(e.selectedDay));
    }, []);

    const toDateChangeHandler = React.useCallback((e: any) => {
        setTDValue(convertDateToDateOnlyString(e.selectedDay));
    }, []);



    const handleStatusChange = React.useCallback((e: any) => {
        setStatus(e.target.value);
    }, []);

    const handleTotalOperatorChange = React.useCallback((e: any) => {
        setOperatorAmountValue(e.target.value);
    }, []);

    const optionsStatus = orderStatusMappings || [];
    const uniqueOptions = new Map<string, string[]>();
    for (const optionmap of optionsStatus) {
        const erpOrderStatuses = uniqueOptions.get(optionmap.displayName) || [];
        erpOrderStatuses.push(optionmap.erpOrderStatus);
        uniqueOptions.set(optionmap.displayName, erpOrderStatuses);
    }

    const valueStatus =
        parameter.status && parameter.status.length > 0 ? parameter.status[0] : "";

    const valueOperator = parameter.orderTotalOperator || "";

    const handleOrderTotalChange = React.useCallback((e: any) => {
        const noRegex = /^[0-9]*(\.[0-9]{0,2})?$/;
        if (noRegex.test(e.target.value)) {
            setValueOrderTotal(e.target.value);
        }
    }, []);

    return (
        <SearchFieldWrapper extendedStyles={styles.wrapper}>
            <Typography as="div" data-test-selector="filter-search">
                <DynamicDropdown
                    {...styles.searchDynamicDropdown}
                    label={translate("Product")}
                    onSelectionChange={onSelectionChangeHandler}
                    onInputChange={onInputChanged}
                    onKeyPress={checkInputValue}
                    hideNoOptionsIfEmptySearch={true}
                    filterOption={() => true}
                    selected={productErpNumber}
                    options={options}
                    placeholder={translate("Enter keyword, model, brand or part #")}
                />
                <Icon className="search-icon" src={Search} />
            </Typography>

            <TextField
                data-test-selector="orderHistory_filterPoNumber"
                value={poNumberValue}
                label="PO #"
                type="text"
                placeholder=""
                onChange={handlePOChange}
            />
            <TextField
                data-test-selector="orderHistory_filterOrderNumber"
                value={orderNumber}
                label="Order #"
                type="text"
                placeholder=""
                onChange={handleOrderNumberChange}
            />
            <StyledWrapper {...styles.datePickersWrapper} data-test-selector="orderHistory_filterDateRange">
                <DatePicker
                    data-test-selector="orderHistory_filterFromDate"
                    {...styles.fromDate}
                    label={translate("Date Range")}
                    selectedDay={convertDateOnlyStringToDate(setfromDate)}
                    onDayChange={fromDateChangeHandler}                        
                    dateTimePickerProps={{
                        clearIcon: null,
                        maxDate: toDate,
                        ...styles.fromDate?.dateTimePickerProps,
                    }}
                />
                <DatePicker
                    data-test-selector="orderHistory_filterToDate"
                    {...styles.toDate}
                    label={translate("To")}
                    selectedDay={convertDateOnlyStringToDate(settoDate)}
                    onDayChange={toDateChangeHandler}
                    dateTimePickerProps={{
                        clearIcon: null,
                        minDate: fromDate,
                        ...styles.toDate?.dateTimePickerProps,
                    }}
                />
            </StyledWrapper>


            <Select
                label={translate("Ship To")}
                className="ShipTo"
                value={shipToValue}
                onChange={handleChange}
            >
                <option value="">{translate("Select one")}</option>
                {shipToOptions.map(shipTo => (
                    <option key={shipTo.customerSequence} value={shipTo.customerSequence !== "" ? shipTo.customerSequence : shipTo.label}>
                        {shipTo.label}
                    </option>
                ))}
            </Select>

            <Select
                label={translate("Status")}
                {...styles.select}
                value={statusValue}
                onChange={handleStatusChange}
                data-test-selector="orderHistory_filterStatus"
            >
                <option value="">{translate("Select a status")}</option>
                {Array.from(uniqueOptions, ([name, values]) => ({ name, values })).map(option => (
                    <option key={option.values.join(",")} value={option.values.join(",")}>
                        {option.name}
                    </option>
                ))}
            </Select>

            <Select
                data-test-selector="orderHistory_filterOrderTotalOperator"
                label={translate("Order Total")}
                {...styles.select}
                value={statusOperatorAmountValue}
                onChange={handleTotalOperatorChange}
            >
                <option value="">{translate("Select order total")}</option>
                {orderTotalOperators.map(operator => (
                    <option key={operator} value={operator}>
                        {translate(operator)}
                    </option>
                ))}
            </Select>

            {statusOperatorAmountValue ? (
                <TextField
                    data-test-selector="orderHistory_filterOrderTotal"
                    // {...styles.valueOrderTotalCSS}
                    value={valueOrderTotal}
                    label="Amount"
                    type="number"
                    placeholder="Type a number"
                    onChange={handleOrderTotalChange}
                />
            ):""}

            <Typography as="div" {...styles.customWrapper}>
                <Button onClick={setFilter}
                    {...styles.filterOrderButton}>
                    {translate("Filter Orders")}
                </Button>
                <Button
                    {...styles.clearButton}
                    onClick={clearSearch}
                >
                    {translate("Clear Filters")}
                </Button>
            </Typography>

        </SearchFieldWrapper>
    );

};
function findDisplayName(erpOrderStatus: string, orderStatusMappings: OrderStatusMappingModel[] = []) {
    const arrOfStatus = erpOrderStatus.split(",");

    let returnVal;
    for (let i = 0; i < orderStatusMappings.length; i++) {
        const option = orderStatusMappings[i];
        if (arrOfStatus.includes(option.erpOrderStatus)) {
            returnVal = option.displayName;
            break;
        }
    }
    return returnVal;
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderHistorySearchClearButton),
    definition: {
        group: "Order History",
        displayName: "Order History Filters",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
    },
};

export default widgetModule;
